import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface TestimonialCardProps {
  testimonial: {
    id: number;
    clientName: string;
    clientTitle: string;
    clientImage: string;
    clientImageAlt: string;
    industry: string;
    strategy: string;
    clientType: string;
    testimonialText: string;
    videoThumbnail?: string;
    videoThumbnailAlt?: string;
    hasVideo: boolean;
    performanceMetrics: {
      roi: string;
      timeframe: string;
      portfolioGrowth: string;
    };
    verifiedBadge: boolean;
    dateAdded: string;
  };
  onVideoPlay?: (id: number) => void;
}

const TestimonialCard = ({ testimonial, onVideoPlay }: TestimonialCardProps) => {
  const { t } = useLanguage();
  return (
    <div className="bg-card rounded-lg shadow-card hover:shadow-hover transition-shadow duration-300 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <AppImage
                src={testimonial.clientImage}
                alt={testimonial.clientImageAlt}
                className="w-14 h-14 rounded-full object-cover"
              />
              {testimonial.verifiedBadge && (
                <div className="absolute -bottom-1 -right-1 bg-success rounded-full p-1">
                  <Icon name="CheckBadgeIcon" size={14} variant="solid" className="text-white" />
                </div>
              )}
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground text-base">
                {testimonial.clientName}
              </h3>
              <p className="text-sm text-muted-foreground">{testimonial.clientTitle}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{testimonial.industry}</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
              {testimonial.strategy}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {testimonial.clientType}
            </span>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-foreground text-sm leading-relaxed line-clamp-4">
            "{testimonial.testimonialText}"
          </p>
        </div>

        {testimonial.hasVideo && testimonial.videoThumbnail && (
          <div className="relative mb-4 rounded-lg overflow-hidden group cursor-pointer" onClick={() => onVideoPlay?.(testimonial.id)}>
            <AppImage
              src={testimonial.videoThumbnail}
              alt={testimonial.videoThumbnailAlt || `Video testimonial thumbnail for ${testimonial.clientName}`}
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Icon name="PlayIcon" size={28} variant="solid" className="text-primary ml-1" />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
          <div className="text-center">
            <p className="text-2xl font-heading font-bold text-success">
              {testimonial.performanceMetrics.roi}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('clientSuccess.roi')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-heading font-bold text-primary">
              {testimonial.performanceMetrics.timeframe}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('clientSuccess.timeframe')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-heading font-bold text-accent">
              {testimonial.performanceMetrics.portfolioGrowth}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('clientSuccess.portfolioGrowth')}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>Added {testimonial.dateAdded}</span>
          <button className="flex items-center space-x-1 text-primary hover:text-primary/80 transition-colors font-medium">
            <span>Read Full Story</span>
            <Icon name="ArrowRightIcon" size={14} variant="outline" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCard;